import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { PAYG_CONTACT_PRICE_USD } from './import-charge-confirm';

export interface ImportChargeConfirmData {
  /** New contacts the user agrees to pay for (upper bound). */
  count: number;
  /** Most it can cost, in dollars. */
  amountUsd: number;
  /** The app's language (TranslateService.currentLang), so numbers read the way the text does. */
  lang?: string;
}

/**
 * BCP 47 locale for a 2-letter app language; undefined lets Intl fall back to the browser's.
 * Bare "es" resolves to Spain's period grouping ("3.696"); Rod decided (card #608) the app's
 * Spanish text uses a comma instead, Latin American style, hence the "es-MX" override.
 */
const localeOf = (lang: unknown): string | undefined => {
  if (typeof lang !== 'string' || !/^[a-z]{2}$/i.test(lang)) return undefined;
  return lang.toLowerCase() === 'es' ? 'es-MX' : lang;
};

/**
 * USD in the app's language: "$2,000" (en), "2 000 $US" (fr), "2,000 US$" (es); cents only when
 * there are some ("$0.05"). minimumFractionDigits is set together with maximumFractionDigits:
 * older Intl engines throw a RangeError when the currency's default minimum (2) exceeds the
 * maximum. Any Intl failure falls back to plain "$2000" so the dialog always opens.
 */
export function formatUsd(v: number, locale?: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
  } catch {
    return `$${v}`;
  }
}

export function formatCount(v: number, locale?: string): string {
  try {
    return new Intl.NumberFormat(locale).format(v);
  } catch {
    return String(v);
  }
}

/**
 * Card #389: "You'll be charged up to $2,000 (2,000 contacts × $1)." Cancel (or closing the
 * window) sends nothing; only "Import and charge" answers true.
 */
@Component({
  selector: 'app-import-charge-confirm-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, TranslateModule],
  templateUrl: './import-charge-confirm-dialog.component.html',
})
export class ImportChargeConfirmDialogComponent {
  /** Translation params, numbers formatted for reading ("2,000", "$2,000"). */
  readonly params: { count: string; amount: string; price: string };

  constructor(
    public dialogRef: MatDialogRef<ImportChargeConfirmDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: ImportChargeConfirmData,
  ) {
    const count = Number.isFinite(data?.count) ? data.count : 0;
    const amount = Number.isFinite(data?.amountUsd) ? data.amountUsd : 0;
    const locale = localeOf(data?.lang);
    this.params = {
      count: formatCount(count, locale),
      // Red so the charged amount stands out; the message binds [innerHTML] to render this span.
      amount: `<span class="text-danger">${formatUsd(amount, locale)}</span>`,
      price: formatUsd(PAYG_CONTACT_PRICE_USD, locale),
    };
  }

  answer(ok: boolean): void {
    this.dialogRef.close(ok);
  }
}
